import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Button,
  Tabs, Tab, Box, Typography, Grid, Chip, Divider, Skeleton, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GavelIcon from '@mui/icons-material/Gavel';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { monitoramentoService } from '../../services/api';

const CORES_RISCO: Record<string, string> = {
  CRITICO: '#E74C3C', ALTO: '#F39C12', MEDIO: '#F1C40F', BAIXO: '#27AE60',
};

const CORES_SITUACAO: Record<string, string> = {
  REGULAR: '#27AE60', IRREGULAR: '#F39C12', INTERDITADO: '#E74C3C', FECHADO: '#95A5A6',
};

const CORES_RESULTADO: Record<string, string> = {
  REGULAR: '#27AE60', IRREGULAR: '#E74C3C', PARCIAL: '#F39C12',
};

const CORES_MEDIDA_STATUS: Record<string, string> = {
  VIGENTE: '#E74C3C', CUMPRIDA: '#27AE60', REVOGADA: '#95A5A6', EXPIRADA: '#7F8C8D',
};

const CORES_ALVARA: Record<string, string> = {
  VIGENTE: '#27AE60', VENCIDO: '#E74C3C', SUSPENSO: '#F39C12', CASSADO: '#8E44AD', PENDENTE: '#95A5A6',
};

const CORES_DENUNCIA: Record<string, string> = {
  ABERTA: '#3498DB', EM_APURACAO: '#F39C12', CONCLUIDA: '#27AE60', ARQUIVADA: '#95A5A6',
};

interface RegistroIntegradoModalProps {
  open: boolean;
  onClose: () => void;
  municipioId: number;
  estabelecimentoId: number | null;
}

interface Estabelecimento {
  id: number; cnpj?: string; razao_social?: string; nome_fantasia?: string;
  atividade_principal?: string; setor?: string; endereco?: string; bairro?: string;
  regional?: string; latitude?: number; longitude?: number;
  cnae_codigo?: string; inscricao_municipal?: string; responsavel_tecnico?: string;
  responsavel_tecnico_registro?: string; area_m2?: number; telefone?: string;
  email?: string; porte?: string;
  alvara_numero?: string; alvara_emissao?: string; alvara_validade?: string; alvara_status?: string;
  nivel_risco?: string; ultima_inspecao?: string; proxima_inspecao?: string;
  resultado_ultima_inspecao?: string;
  medida_cautelar?: boolean; medida_cautelar_tipo?: string; medida_cautelar_data?: string;
  situacao?: string; data_cadastro?: string;
}

interface Inspecao {
  id: number; data: string; tipo: string; resultado: string;
  fiscal?: string; observacoes?: string; auto_infracao?: string;
}

interface Medida {
  id: number; data: string; tipo: string; descricao?: string; status: string;
  vigencia_inicio?: string; vigencia_fim?: string; processo?: string; fundamento_legal?: string;
}

interface Denuncia {
  id: number; protocolo?: string; data: string; origem?: string; descricao?: string;
  situacao: string; providencia?: string; data_conclusao?: string;
}

interface RegistroData {
  estabelecimento: Estabelecimento | null;
  inspecoes: Inspecao[];
  medidas: Medida[];
  denuncias: Denuncia[];
}

const Field: React.FC<{ label: string; value?: React.ReactNode; sm?: number }> = ({ label, value, sm = 6 }) => (
  <Grid item xs={12} sm={sm}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value || '-'}</Typography>
  </Grid>
);

const RegistroIntegradoModal: React.FC<RegistroIntegradoModalProps> = ({
  open, onClose, municipioId, estabelecimentoId,
}) => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<RegistroData | null>(null);

  useEffect(() => {
    if (!open || !estabelecimentoId) return;
    setLoading(true);
    setError('');
    setTab(0);
    monitoramentoService.getVisaGeoRegistroIntegrado(municipioId, estabelecimentoId)
      .then((res: RegistroData) => setData(res))
      .catch((err: any) => setError(err?.response?.data?.message || 'Erro ao carregar registro integrado'))
      .finally(() => setLoading(false));
  }, [open, municipioId, estabelecimentoId]);

  const estab = data?.estabelecimento;
  const inspecoes = data?.inspecoes || [];
  const medidas = data?.medidas || [];
  const denuncias = data?.denuncias || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Registro Integrado</Typography>
          {estab && (
            <Typography variant="body2" color="text.secondary">
              {estab.nome_fantasia || estab.razao_social} {estab.cnpj && `• CNPJ ${estab.cnpj}`}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <Divider />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: '1px solid #eee' }}>
        <Tab icon={<BusinessIcon />} iconPosition="start" label="Dados Cadastrais" />
        <Tab icon={<AssignmentIcon />} iconPosition="start" label="Alvara" />
        <Tab icon={<FactCheckIcon />} iconPosition="start" label={`Inspecoes (${inspecoes.length})`} />
        <Tab icon={<GavelIcon />} iconPosition="start" label={`Medidas Cautelares (${medidas.length})`} />
        <Tab icon={<ReportProblemIcon />} iconPosition="start" label={`Denuncias (${denuncias.length})`} />
      </Tabs>
      <DialogContent sx={{ minHeight: 400 }}>
        {loading && <Stack spacing={1}>{[1,2,3,4].map(i => <Skeleton key={i} variant="rounded" height={60} />)}</Stack>}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && estab && (
          <>
            {/* TAB 0: Dados Cadastrais */}
            {tab === 0 && (
              <Box sx={{ pt: 1 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                  {estab.nivel_risco && (
                    <Chip label={`Risco ${estab.nivel_risco}`} size="small"
                      sx={{ bgcolor: CORES_RISCO[estab.nivel_risco] || '#999', color: '#fff', fontWeight: 600 }} />
                  )}
                  {estab.situacao && (
                    <Chip label={estab.situacao} size="small"
                      sx={{ bgcolor: CORES_SITUACAO[estab.situacao] || '#999', color: '#fff', fontWeight: 600 }} />
                  )}
                  {estab.porte && <Chip label={`Porte ${estab.porte}`} size="small" variant="outlined" />}
                </Stack>
                <Typography variant="overline" color="text.secondary">Identificacao</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Field label="CNPJ" value={estab.cnpj} />
                  <Field label="Inscricao Municipal" value={estab.inscricao_municipal} />
                  <Field label="Razao Social" value={estab.razao_social} sm={12} />
                  <Field label="Nome Fantasia" value={estab.nome_fantasia} />
                  <Field label="Setor" value={estab.setor} />
                  <Field label="CNAE Principal" value={`${estab.cnae_codigo || ''} — ${estab.atividade_principal || ''}`} sm={12} />
                </Grid>
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" color="text.secondary">Endereco</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Field label="Endereco" value={estab.endereco} sm={12} />
                  <Field label="Bairro" value={estab.bairro} />
                  <Field label="Regional" value={estab.regional} />
                  <Field label="Latitude" value={estab.latitude} sm={3} />
                  <Field label="Longitude" value={estab.longitude} sm={3} />
                </Grid>
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" color="text.secondary">Contato e Responsavel</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Field label="Telefone" value={estab.telefone} />
                  <Field label="Email" value={estab.email} />
                  <Field label="Responsavel Tecnico" value={estab.responsavel_tecnico} />
                  <Field label="Registro Profissional" value={estab.responsavel_tecnico_registro} />
                  <Field label="Area (m2)" value={estab.area_m2 ? `${estab.area_m2} m2` : '-'} />
                  <Field label="Data de Cadastro" value={estab.data_cadastro} />
                </Grid>
              </Box>
            )}

            {/* TAB 1: Alvara */}
            {tab === 1 && (
              <Box sx={{ pt: 1 }}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    {estab.alvara_status && (
                      <Chip label={estab.alvara_status} size="small"
                        sx={{ bgcolor: CORES_ALVARA[estab.alvara_status] || '#999', color: '#fff', fontWeight: 600 }} />
                    )}
                  </Stack>
                  <Grid container spacing={2}>
                    <Field label="Numero do Alvara" value={estab.alvara_numero} />
                    <Field label="Status" value={estab.alvara_status} />
                    <Field label="Data de Emissao" value={estab.alvara_emissao} />
                    <Field label="Data de Validade" value={estab.alvara_validade} />
                  </Grid>
                </Paper>
                <Typography variant="overline" color="text.secondary">Resumo da Inspecao Atual</Typography>
                <Grid container spacing={2}>
                  <Field label="Ultima Inspecao" value={estab.ultima_inspecao} />
                  <Field label="Proxima Inspecao" value={estab.proxima_inspecao} />
                  <Field label="Resultado" value={estab.resultado_ultima_inspecao} />
                  <Field label="Nivel de Risco" value={estab.nivel_risco} />
                </Grid>
              </Box>
            )}

            {/* TAB 2: Inspecoes */}
            {tab === 2 && (
              <Box sx={{ pt: 1 }}>
                {inspecoes.length === 0 ? (
                  <Alert severity="info">Nenhuma inspecao registrada</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Data</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Resultado</TableCell>
                          <TableCell>Fiscal</TableCell>
                          <TableCell>Auto de Infracao</TableCell>
                          <TableCell>Observacoes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {inspecoes.map(i => (
                          <TableRow key={i.id} hover>
                            <TableCell>{i.data}</TableCell>
                            <TableCell><Chip label={i.tipo} size="small" variant="outlined" /></TableCell>
                            <TableCell>
                              <Chip label={i.resultado} size="small"
                                sx={{ bgcolor: CORES_RESULTADO[i.resultado] || '#999', color: '#fff', fontSize: 11 }} />
                            </TableCell>
                            <TableCell>{i.fiscal || '-'}</TableCell>
                            <TableCell>{i.auto_infracao || '-'}</TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>{i.observacoes || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* TAB 3: Medidas */}
            {tab === 3 && (
              <Box sx={{ pt: 1 }}>
                {medidas.length === 0 ? (
                  <Alert severity="info">Nenhuma medida cautelar registrada</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Data</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Vigencia</TableCell>
                          <TableCell>Processo</TableCell>
                          <TableCell>Descricao</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {medidas.map(m => (
                          <TableRow key={m.id} hover>
                            <TableCell>{m.data}</TableCell>
                            <TableCell>{m.tipo}</TableCell>
                            <TableCell>
                              <Chip label={m.status} size="small"
                                sx={{ bgcolor: CORES_MEDIDA_STATUS[m.status] || '#999', color: '#fff', fontSize: 11 }} />
                            </TableCell>
                            <TableCell>{m.vigencia_inicio || '-'} a {m.vigencia_fim || '-'}</TableCell>
                            <TableCell>{m.processo || '-'}</TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>
                              {m.descricao || '-'}
                              {m.fundamento_legal && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Fundamento: {m.fundamento_legal}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* TAB 4: Denuncias */}
            {tab === 4 && (
              <Box sx={{ pt: 1 }}>
                {denuncias.length === 0 ? (
                  <Alert severity="info">Nenhuma denuncia registrada</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Protocolo</TableCell>
                          <TableCell>Data</TableCell>
                          <TableCell>Origem</TableCell>
                          <TableCell>Situacao</TableCell>
                          <TableCell>Descricao</TableCell>
                          <TableCell>Providencia</TableCell>
                          <TableCell>Conclusao</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {denuncias.map(d => (
                          <TableRow key={d.id} hover>
                            <TableCell>{d.protocolo || '-'}</TableCell>
                            <TableCell>{d.data}</TableCell>
                            <TableCell>{d.origem || '-'}</TableCell>
                            <TableCell>
                              <Chip label={d.situacao.replace('_', ' ')} size="small"
                                sx={{ bgcolor: CORES_DENUNCIA[d.situacao] || '#999', color: '#fff', fontSize: 11 }} />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 260 }}>{d.descricao || '-'}</TableCell>
                            <TableCell sx={{ maxWidth: 220 }}>{d.providencia || '-'}</TableCell>
                            <TableCell>{d.data_conclusao || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </>
        )}
        {!loading && !error && !estab && (
          <Alert severity="warning">Estabelecimento nao encontrado</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistroIntegradoModal;
